<template>
  <div class="flex w-auto max-w-sm flex-col rounded-md transition-shadow hover:shadow-md">
    <a
      :href="`/products/${product.id}`"
      class="relative flex w-full flex-col items-center justify-between"
      :class="{ 'cursor-default opacity-50': productOutOfStock }"
    >
      <div
        class="relative mb-2 flex h-36 w-full items-center justify-center overflow-hidden rounded-t-md sm:h-40 md:h-44"
        style="transform-origin: center center"
      >
        <div
          :style="{ 'background-image': `url(${productImageToShow})` }"
          class="absolute size-full bg-cover bg-center object-cover duration-300 ease-in-out hover:scale-105"
        />
        <product-like-button
          v-if="sessionStore.user?.kind === 'shop' || sessionStore.user?.kind === 'super_admin'"
          :product-id="product.id"
          :initial-is-liked="true"
          button-class="absolute right-2 top-2 z-20"
        />
        <p
          v-if="productOutOfStock"
          class="z-10 w-full self-start rounded-t-md bg-lk-light-gray p-1 text-center text-xs text-white md:p-2 md:text-sm"
        >
          Agotado
        </p>
      </div>
      <div class="flex w-full flex-col p-4 text-left text-sm text-gray-700 md:text-base">
        <p class="line-clamp-1 hover:line-clamp-none">
          {{ product.name.charAt(0).toUpperCase() + product.name.slice(1).toLowerCase() }}
        </p>
        <p class="mb-2 text-xs font-light md:text-sm">
          {{ product.makerName }}
        </p>
        <div class="flex w-full flex-col">
          <div class="flex justify-between text-xs font-light md:text-sm">
            <span>Precio Mayorista:</span>
            <span>{{ useFormatCurrency(product.wholesalePriceCents) }}</span>
          </div>
          <div class="flex justify-between text-xs font-light md:text-sm">
            <span>Precio Público:</span>
            <span>{{ useFormatCurrency(product.unitaryPriceCents) }}</span>
          </div>
          <div class="relative mb-5 flex items-center">
            <div class="z-30 flex w-full justify-between text-xs font-light md:text-sm">
              <lokal-tooltip
                main-label="Márgen:"
                :position-options="'left'"
                information-classes="w-40 p-1 md:p-2 md:w-64 text-xs md:text-sm z-50"
                button-classes="flex items-center space-x-1"
                :information="marginInformation"
              />
              <span>{{ margin }}%</span>
            </div>
          </div>
        </div>
      </div>
    </a>
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue';
import useFormatCurrency from './use/format-currency';
import useProductImage from './use/product-image';
import ProductLikeButton from './product-like-button.vue';
import useSessionStore from 'stores/user-store';
import { round } from 'lodash';
import lokalTooltip from './shared/lokal-tooltip.vue';
import useBillingTools from './use/billing-tools';
import useProductWholesalePriceMethods from './use/product-wholesale-price';
import useCartStore from 'stores/cart';

interface ProductCardProps {
  product: Product;
}

const TOTAL_PORCENTAGE = 100;
const props = defineProps<ProductCardProps>();
const sessionStore = useSessionStore();
const quantity = computed(() => cartStore.items[props.product.id]?.quantity || 0);
const { productWholesalePrice } = useProductWholesalePriceMethods(props.product, quantity);
const { billTools, convertAdditionalTax, additionalTaxInitials } = useBillingTools();
const currentAdditionalTax = computed(() => convertAdditionalTax(props.product.additionalTax));
const additionalTaxValue = computed(() => round(productWholesalePrice.value * currentAdditionalTax.value));
const netWholesalePriceWithAdditionalTax = computed(() => productWholesalePrice.value + additionalTaxValue.value);
const cartStore = useCartStore();

const productOutOfStock = computed(() => {
  return (
    props.product.status === 'out_of_stock' ||
    (props.product.inventory?.enable && props.product.inventory?.quantity === 0)
  );
});

const marginInformation = computed(() => {
  if (currentAdditionalTax.value > 0) {
    return `
    (Precio Público - (Precio Mayorista + ${additionalTaxInitials(props.product.additionalTax)})) / Precio Público <br>
    Ambos precios sin IVA.
  `;
  }

  return `
    (Precio Público - Precio Mayorista) / Precio Público <br>
    Ambos precios sin IVA.
  `;
});

const margin = computed(() => {
  if (props.product.unitaryPriceCents === 0) return 0;

  const netUnitaryPrice = round(props.product.unitaryPriceCents / billTools.value.ivaValue);
  const diff = netUnitaryPrice - netWholesalePriceWithAdditionalTax.value;

  return round((diff / netUnitaryPrice) * TOTAL_PORCENTAGE);
});

const { productImageToShow } = useProductImage(props.product, 'jpgMd');
</script>
